import { inject, injectable } from "inversify";
import type { IUserStore } from "@whatslly/ui-components/services/UserStore";
import { InjectUserStore } from "@whatslly/ui-components/services/UserStore";
import { UrlStore } from "../../../../services/urlStore";
import type { IOrganizationStore } from "../../../../services/IOrganizationStore";
import { InjectOrganizationStore } from "../../../../services/IOrganizationStore";
import { FiltersStore } from "../../../../services/filtersStore";
import { parseOrReturnError } from "@whatslly/commons/parseOrReturnError";
import { action, computed, makeObservable, observable, reaction, runInAction } from "mobx";
import { deleteJSON, getJSON, postJSON, putJSON } from "@whatslly/commons/apiUtils";
import { modalService } from "@whatslly/ui-components/modals";
import Toast from "@whatslly/ui-components/v2/Toaster/Toast";
import { FrontLogger } from "@whatslly/commons/frontLogger/frontLogger";
import { hasPermission } from "@whatslly/commons/platformPermissions";
import { UserPermissions } from "@whatslly/commons/granularPermissions";
import { TFunction } from "i18next";
import { loginListener } from "@whatslly/commons/loginListener";
import { LoginCommands } from "@whatslly/commons/loginCommands";
import type { GetAppsRedirectUrlResponseType } from "@whatslly/types/integrations/auth/res/GetAppsRedirectUrlResponse";
import {
  GetPlatformsResponse,
  GetSlackFleetResponse,
  ImPlatform,
  InstallPlatformResponse,
  SlackFleetAppInfo,
} from "@whatslly/types/tuvis-messaging/TuvisMessagingPlatforms";
import type { GetAppsTokenResponsType } from "@whatslly/types/integrations/auth/res/GetAppsTokenResponse";
import { PLATFORM_TYPES } from "@whatslly/types/tuvis-messaging/TuvisMessagingPlatforms";
import { ConfigureShortcutModal } from "./ConfigureShortcutModal";
import { UninstallSlackModal } from "./UninstallSlackModal";
@injectable()
export class SlackSettingsStore {
  isInitialized = false;
  connected = false;
  isConnecting = false;
  platform?: ImPlatform;
  fleetApps: SlackFleetAppInfo[] = [];
  constructor(
    @inject(InjectUserStore) private readonly userStore: IUserStore,
    @inject(FiltersStore) private readonly filtersStore: FiltersStore,
    @inject(UrlStore) private readonly urlStore: UrlStore,
    @inject(InjectOrganizationStore) private readonly orgStore: IOrganizationStore,
    @inject(FrontLogger) private readonly logger: FrontLogger
  ) {
    makeObservable(this, {
      isInitialized: observable,
      connected: observable,
      isConnecting: observable,
      platform: observable,
      fleetApps: observable,
      isReadOnlySettingsUser: computed,
      workerApps: computed,
      mainInstalled: computed,
      handleSlackOAuth: action.bound,
      filters: computed,
      init: action.bound,
    });
  }

  get isReadOnlySettingsUser() {
    return !hasPermission(this.userStore.user, UserPermissions["slack.update_settings"]);
  }

  get orgId() {
    return this.filtersStore.orgId || this.userStore.user?.organizationId;
  }

  get filters() {
    return { orgId: this.orgId ?? "" };
  }

  async getOrgId() {
    const org = await this.orgStore.selectedOrg;
    return org?.value ?? this.userStore.user?.organizationId;
  }

  async init() {
    try {
      const orgId = await this.getOrgId();
      const authorized = hasPermission(this.userStore.user, UserPermissions["slack.update_settings"] | UserPermissions["slack.view_settings"]);
      if (!orgId || !authorized) {
        this.connected = false;
        this.isInitialized = true;
        return;
      }
      const res = await getJSON({
        urlSuffix: `/integrations/platforms?${new URLSearchParams(this.filters).toString()}`,
        pod: this.userStore.pod,
        etoken: this.userStore.etoken,
      }).then((res) => parseOrReturnError<GetPlatformsResponse>(res, this.logger));

      await this.loadFleetApps();

      runInAction(() => {
        const slackPlatforms =
          res.success && Array.isArray(res.platforms) ? res.platforms.filter((p) => p.platformType === PLATFORM_TYPES.SLACK) : [];
        const primaryAppId = this.fleetApps.find((a) => a.primary)?.appId;
        const mainPlatform = (primaryAppId && slackPlatforms.find((p) => p.appId === primaryAppId)) || slackPlatforms[0];
        this.connected = !!mainPlatform && mainPlatform.active;
        this.platform = this.connected ? mainPlatform : undefined;
        this.isInitialized = true;
      });
    } catch (err: any) {
      this.logger.error({ err }, "Failed to check Slack connection status");
      runInAction(() => {
        this.connected = false;
        this.isInitialized = true;
      });
    }
  }

  async handleSlackOAuth(t: TFunction, slackAppId?: string) {
    runInAction(() => {
      this.isConnecting = true;
    });

    try {
      const urlSearchParams = new URLSearchParams();
      urlSearchParams.set("appName", "slack");
      urlSearchParams.set("chatApp", "popup-web");
      urlSearchParams.set("userId", "");
      if (slackAppId) {
        urlSearchParams.set("integrationParams", JSON.stringify({ slackAppId }));
      }
      const getRedRes = await getJSON({
        urlSuffix: `/integrations/auth/redirect?${urlSearchParams.toString()}`,
        pod: this.userStore.pod,
      }).then((res) => parseOrReturnError<GetAppsRedirectUrlResponseType>(res, this.logger));
      if (!getRedRes.success) {
        throw new Error(t("slack_settings_page.connection_failed"));
      }

      const top = (window.screen.availHeight - 500) / 2;
      const left = (window.screen.availWidth - 500) / 2;
      const options: Record<string, number | boolean> = {
        width: 800,
        height: 800,
        top: top > 0 ? top : 0,
        left: left > 0 ? left : 0,
        location: true,
        resizable: true,
        statusbar: true,
        toolbar: false,
      };

      const winOpt = Object.keys(options)
        .map((op) => `${op}=${Number(options[op])}`)
        .join(",");
      const popup = window.open(getRedRes.url, "Slack OAuth", winOpt);

      if (!popup) {
        modalService.openModal(Toast, { text: t("slack_settings_page.popup_blocked"), type: "error" });
        runInAction(() => {
          this.isConnecting = false;
        });
        return;
      }

      const appTokensRes = await new Promise<any & { success: true }>((resolve, reject) => {
        const unsubscribe = loginListener.on(LoginCommands.LOGIN_COMPLETED, (data) => {
          clearInterval(interval);
          resolve(data);
          unsubscribe();
        });
        const interval = setInterval(() => {
          if (popup?.closed) {
            clearInterval(interval);
            unsubscribe();
            reject(new Error(t("slack_settings_page.oauth_cancelled")));
          }
        }, 1000);
      });

      const getAppTokensRes = await postJSON({
        urlSuffix: "/integrations/auth/get-apps-token",
        pod: this.userStore.pod,
        body: JSON.stringify({
          code: appTokensRes.code,
          state: appTokensRes.state,
        }),
      }).then((res) => parseOrReturnError<GetAppsTokenResponsType>(res, this.logger));

      if (!getAppTokensRes.success) {
        throw new Error(getAppTokensRes.error || t("slack_settings_page.connection_failed"));
      }

      const installRes = await postJSON({
        urlSuffix: `/integrations/platforms/install?${new URLSearchParams(this.filters).toString()}`,
        pod: this.userStore.pod,
        etoken: this.userStore.etoken ?? "",
        body: JSON.stringify({
          platformType: "slack",
          tokens: getAppTokensRes.tokens,
          integrationData: getAppTokensRes.integrationData,
        }),
      }).then((res) => parseOrReturnError<InstallPlatformResponse>(res, this.logger));

      if (!installRes.success) {
        throw new Error(installRes.error || t("slack_settings_page.connection_failed"));
      }

      runInAction(() => {
        this.isConnecting = false;
      });
      modalService.openModal(Toast, { text: t("slack_settings_page.connected_success"), type: "regular" });
      this.init();
    } catch (err: any) {
      runInAction(() => {
        this.isConnecting = false;
      });
      modalService.openModal(Toast, { text: err.message || t("slack_settings_page.connection_failed"), type: "error" });
    }
  }

  async disconnect(platformId = this.platform?._id) {
    await deleteJSON({
      urlSuffix: `/integrations/platforms/${platformId}?${new URLSearchParams(this.filters).toString()}`,
      etoken: this.userStore.etoken ?? "",
      pod: this.userStore.pod,
      body: JSON.stringify({}),
    });

    await this.init();
  }

  get workerApps(): SlackFleetAppInfo[] {
    return this.fleetApps.filter((app) => !app.primary);
  }

  get mainInstalled(): boolean {
    return this.fleetApps.find((app) => app.primary)?.installed ?? this.connected;
  }

  private async loadFleetApps() {
    try {
      const res = await getJSON({
        urlSuffix: `/integrations/platforms/fleet?${new URLSearchParams(this.filters).toString()}`,
        pod: this.userStore.pod,
        etoken: this.userStore.etoken,
      }).then((r) => parseOrReturnError<GetSlackFleetResponse>(r, this.logger));
      runInAction(() => {
        this.fleetApps = res.success ? res.apps : [];
      });
    } catch (err: any) {
      this.logger.error({ err }, "Failed to load Slack fleet apps");
      runInAction(() => {
        this.fleetApps = [];
      });
    }
  }

  async openWorkerUninstallModal(t: TFunction, app: SlackFleetAppInfo) {
    await this.openUninstallModal(t, app.name, app.platformId);
  }

  async openConfigureShortcutModal(t: TFunction) {
    const modal = modalService.openModal(ConfigureShortcutModal, {
      shortcutUrl: this.platform?.metadata?.whatsappTemplateShortcutTrigger,
    });
    const result = await modal.promise;
    if (result?.shortcutUrl) {
      try {
        const updateRes = await putJSON({
          urlSuffix: `/integrations/platforms/${this.platform?._id}/metadata?${new URLSearchParams(this.filters).toString()}`,
          pod: this.userStore.pod,
          etoken: this.userStore.etoken ?? "",
          body: JSON.stringify({
            whatsappTemplateShortcutTrigger: result.shortcutUrl,
          }),
        }).then((res) => parseOrReturnError<{ success: boolean }>(res, this.logger));

        if (updateRes.success) {
          modalService.openModal(Toast, {
            text: t("slack_settings_page.configure_shortcut_modal.success"),
            type: "regular",
          });
          this.init();
        } else {
          throw new Error(t("slack_settings_page.configure_shortcut_modal.error"));
        }
      } catch (err: any) {
        modalService.openModal(Toast, {
          text: err.message || t("slack_settings_page.configure_shortcut_modal.error"),
          type: "error",
        });
      }
    }
  }

  async openUninstallModal(t: TFunction, workspaceName = this.platform?.platformWorkspaceName, platformId = this.platform?._id) {
    if (!platformId) return;
    const modal = modalService.openModal(UninstallSlackModal, { workspaceName });
    const result = await modal.promise;

    if (result?.confirmed) {
      try {
        await this.disconnect(platformId);
        modalService.openModal(Toast, {
          text: t("slack_settings_page.uninstall_modal.success"),
          type: "regular",
        });
      } catch (err: any) {
        modalService.openModal(Toast, {
          text: t("slack_settings_page.uninstall_modal.error"),
          type: "error",
        });
      }
    }
  }
}
